import type {SelectedAgent} from '@github-ui/agent-sessions/types/session'
import {sendEvent} from '@github-ui/hydro-analytics'
import type {useNavigate} from '@github-ui/use-navigate'
import type {Icon} from '@primer/octicons-react'
import {PencilIcon, PlusIcon, TrashIcon} from '@primer/octicons-react'

import type {AvailableCommand} from '../hooks/use-available-commands'
import type {CopilotChatManager} from './copilot-chat-manager'
import type {CopilotChatState} from './copilot-chat-reducer'
import type {AgentDropdownRepository} from './copilot-chat-types'
import {copilotFeatureFlags} from './copilot-feature-flags'
import {getSelectedThread} from './get-selected-thread'

interface SlashCommandParams {
  manager: CopilotChatManager
  chatState: CopilotChatState
  availableCommands: AvailableCommand[]
}

export type SlashCommand = {
  action: (
    params: string,
    state: CopilotChatState,
    navigate: ReturnType<typeof useNavigate>,
  ) => Promise<boolean> | boolean
  label?: string
  key: string // used for search
  icon?: Icon
}

export const SLASH_COMMAND_PREFIX = '/'

export const getSlashCommands = ({manager, chatState, availableCommands}: SlashCommandParams): SlashCommand[] => {
  const slashCommands: SlashCommand[] =
    chatState.mode === 'assistive'
      ? [
          {
            label: 'Delete chat',
            key: 'delete',
            icon: TrashIcon,
            action: async (_params, state) => {
              manager.dispatch({type: 'SLASH_COMMANDS_LOADING'})
              try {
                const selectedThread = getSelectedThread(state)
                if (!selectedThread) throw new Error('Thread not found')
                await manager.deleteThread(selectedThread)
                sendEvent('dotcom_chat.activate', {target: 'SLASH_COMMAND_DELETE_THREAD', mode: state.mode})
                manager.dispatch({type: 'SLASH_COMMANDS_LOADED'})
              } catch {
                manager.dispatch({type: 'SLASH_COMMANDS_ERROR'})
              }
              return true
            },
          },
          {
            label: 'Rename conversation',
            key: 'rename',
            icon: PencilIcon,
            action: async (params, state) => {
              manager.dispatch({type: 'SLASH_COMMANDS_LOADING'})
              try {
                const selectedThread = getSelectedThread(state)
                if (!selectedThread) throw new Error('Thread not found')
                await manager.renameThread(selectedThread, params)
                sendEvent('dotcom_chat.activate', {target: 'SLASH_COMMAND_RENAME', mode: state.mode})
                manager.dispatch({type: 'SLASH_COMMANDS_LOADED'})
              } catch {
                manager.dispatch({type: 'SLASH_COMMANDS_ERROR'})
              }
              return true
            },
          },
          {
            label: 'Start a new conversation',
            key: 'new',
            icon: PlusIcon,
            action: async (_params, state) => {
              manager.dispatch({type: 'SLASH_COMMANDS_LOADING'})
              try {
                await manager.selectThread(null)
                sendEvent('dotcom_chat.activate', {target: 'SLASH_COMMAND_NEW_THREAD', mode: state.mode})
                manager.dispatch({type: 'SLASH_COMMANDS_LOADED'})
              } catch {
                manager.dispatch({type: 'SLASH_COMMANDS_ERROR'})
              }
              return true
            },
          },
        ]
      : []

  if (chatState.mode === 'immersive' && copilotFeatureFlags.chatInputCommands) {
    for (const command of availableCommands) {
      if (command.execute) {
        slashCommands.push({
          key: command.key,
          action: async (params, state, navigate) => {
            return await command.execute!(params, state, navigate, manager)
          },
        })
      }
    }
  }

  return slashCommands
}

export interface SlashCommandContext {
  selectedRepo?: AgentDropdownRepository
  selectedBranch?: string
  selectedAgent?: SelectedAgent
}

export const executePotentialSlashCommand = async (
  input: string,
  state: CopilotChatState,
  manager: CopilotChatManager,
  navigate: ReturnType<typeof useNavigate>,
  availableCommands: AvailableCommand[],
  activeModeName?: string,
  context?: SlashCommandContext,
): Promise<boolean> => {
  if (!copilotFeatureFlags.askModeDropdown) {
    if ((input[0] ?? '') !== SLASH_COMMAND_PREFIX) return false
    // We know the first char is SLASH_COMMAND_PREFIX so we can safely remove it
    input = input.substring(1)
  }

  const slashCommands = getSlashCommands({manager, chatState: state, availableCommands})
  const matchingCommands = copilotFeatureFlags.askModeDropdown
    ? slashCommands.filter(slashCommand => slashCommand.key.toLowerCase() === activeModeName?.toLowerCase())
    : slashCommands.filter(slashCommand => {
        const firstInputWord = (input.split(' ', 1)[0] ?? '').toLowerCase().trim()
        return slashCommand.key.toLowerCase() === firstInputWord
      })

  if (matchingCommands.length !== 1) return false
  const matchingCommand = matchingCommands[0]!

  let params = copilotFeatureFlags.askModeDropdown ? input.trim() : input.substring(matchingCommand.key.length).trim()

  // If context is provided, wrap params in JSON with context
  if (context && (context.selectedRepo || context.selectedBranch || context.selectedAgent)) {
    params = JSON.stringify({
      text: params,
      selectedRepo: context.selectedRepo,
      selectedBranch: context.selectedBranch,
      selectedAgent: context.selectedAgent,
    })
  }

  sendEvent('dotcom_chat.activate', {
    target: 'SLASH_COMMAND_EXECUTED',
    mode: state.mode,
    command: matchingCommand.key,
    activeModeName,
  })

  return await matchingCommand.action(params, state, navigate)
}
